import React from 'react';
import { createRoot } from 'react-dom/client';
import { ReactRouterApp } from './routes/app_router';
import { AppLayout } from '@seedlang/layouts';
import { bootstrapPosthogConfig, captureLandingCampaignParams } from '@seedlang/utils';
import './site_color_overrides';

// Auto-recover from rare deploy/cache/network chunk mismatches by doing a single
// guarded reload. Keep detection narrow to avoid masking real errors.
function shouldAutoReloadForChunkError(errorLike) {
  const err = errorLike && (
    errorLike.payload ||
    errorLike.detail ||
    errorLike.error ||
    errorLike.reason ||
    errorLike
  );
  if (!err) return false;

  const code = err.code || (err.reason && err.reason.code);
  const message = String(err.message || (err.reason && err.reason.message) || err);
  const stack = String(err.stack || (err.reason && err.reason.stack) || '');

  if (code === 'CSS_CHUNK_LOAD_FAILED') return true;
  if (message.includes('CSS_CHUNK_LOAD_FAILED')) return true;
  if (message.includes('ChunkLoadError')) return true;
  if (message.includes('Loading CSS chunk') && message.includes('failed')) return true;
  if (message.includes('Unable to preload CSS for')) return true;
  if (message.includes('Failed to fetch dynamically imported module')) return true;
  if (message.includes('Failed to fetch') && stack.includes('preload-helper')) return true;

  return false;
}

function maybeRecoverFromChunkLoadError(errorLike, options = {}) {
  if (!options.forceReload && !shouldAutoReloadForChunkError(errorLike)) return false;
  if (typeof window === 'undefined') return false;
  if (process.env.NODE_ENV !== 'production') return false;

  // Avoid reload loops, and avoid reloading when we already know we're offline.
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return false;

  const key = 'seedlang__chunk_reload_attempted_v1';
  try {
    if (window.sessionStorage && window.sessionStorage.getItem(key)) return false;
    if (window.sessionStorage) window.sessionStorage.setItem(key, String(Date.now()));
  } catch (e) {
    // sessionStorage can throw in some privacy modes; skip reload when we
    // cannot track whether we already retried.
    return false;
  }

  window.location.reload();
  return true;
}

if (typeof window !== 'undefined') {
  window.addEventListener('vite:preloadError', event => {
    if (maybeRecoverFromChunkLoadError(event, { forceReload: true })) {
      event.preventDefault();
    }
  });

  window.addEventListener('unhandledrejection', event => {
    maybeRecoverFromChunkLoadError(event);
  });

  // Some chunk/css load failures surface as 'error' events (resource load).
  window.addEventListener('error', event => {
    maybeRecoverFromChunkLoadError(event);
  });
}

document.addEventListener('DOMContentLoaded', async () => {
  window.layout = new AppLayout(this);
  captureLandingCampaignParams();
  await bootstrapPosthogConfig();
  createRoot(document.getElementById('root')).render(
    <ReactRouterApp />,
  );
});
